import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import ProductScrapeModal from '../components/ProductScrapeModal';
import ProductSearchDropdown from '../components/ProductSearchDropdown';
import { 
  Plus, Package, Tag, Grid3x3, Search, RefreshCw, Eye, 
  Filter, ChevronDown, CheckCircle, XCircle, ExternalLink,
  Smartphone, Laptop, Tablet, Box, TrendingUp, LayoutGrid, List,
  Power, PowerOff, X, Building2, Users, ShoppingCart, Monitor
} from 'lucide-react';

const ProductManagement = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') || 'products';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Mobile Phones');
  const [showScrapeModal, setShowScrapeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filteredVariants, setFilteredVariants] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedDisplayType, setSelectedDisplayType] = useState('all'); // Display Type filter
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, withPrice: 0 });
  
  // Display Type options for filter
  const displayTypes = [
    { value: 'all', label: 'All Displays' },
    { value: 'amoled', label: 'AMOLED', keywords: ['amoled', 'ltpo amoled', 'dynamic amoled', 'super amoled', 'fluid amoled'] },
    { value: 'oled', label: 'OLED', keywords: ['oled', 'p-oled'] },
    { value: 'lcd', label: 'LCD', keywords: ['lcd', 'ips lcd', 'tft lcd', 'ips'] },
    { value: 'retina', label: 'Retina', keywords: ['retina', 'liquid retina'] },
    { value: 'mini-led', label: 'Mini LED', keywords: ['mini led', 'mini-led'] },
  ];
  
  // Channel Status Modal
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [channelModalProduct, setChannelModalProduct] = useState(null);
  const [channelModalAction, setChannelModalAction] = useState('deactivate'); // 'activate' or 'deactivate'
  const [selectedChannels, setSelectedChannels] = useState({ b2b: false, b2r: false, b2c: false, buyback: false });
  const [channelLoading, setChannelLoading] = useState(false);

  const channels = [
    { key: 'b2b', name: 'B2B', description: 'Business to Business', icon: Building2, color: 'blue' },
    { key: 'b2r', name: 'B2R', description: 'Business to Reseller', icon: Users, color: 'purple' },
    { key: 'b2c', name: 'B2C', description: 'Business to Consumer', icon: ShoppingCart, color: 'green' },
    { key: 'buyback', name: 'Buyback', description: 'Buyback Live', icon: RefreshCw, color: 'orange' }
  ];

  const categories = [
    { name: 'Mobile Phones', icon: Smartphone, color: 'blue' },
    { name: 'Laptops', icon: Laptop, color: 'purple' },
    { name: 'Tablets', icon: Tablet, color: 'green' },
    { name: 'Accessories', icon: Box, color: 'orange' }
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/products?tab=${tab}`, { replace: true });
  };

  useEffect(() => {
    fetchBrands();
  }, [selectedCategory]);

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
      fetchBrands();
      // Also fetch variants count for the tab badge
      fetchVariantsCount();
    } else if (activeTab === 'variants') {
      fetchVariants();
    }
  }, [activeTab, selectedCategory]);

  // Calculate stats when products change
  useEffect(() => {
    const total = products.length;
    const active = products.filter(p => p.is_active !== false).length;
    const inactive = products.filter(p => p.is_active === false).length;
    const withPrice = products.filter(p => p.mrp || p.store_prices?.length > 0).length;
    setStats({ total, active, inactive, withPrice });
  }, [products]);

  // Fetch just the count of variants for the tab badge
  const fetchVariantsCount = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-variants`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      const data = await response.json();
      setVariants(data.variants || []);
    } catch (error) {
      console.error('Error fetching variants count:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/brands?category=${selectedCategory}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      const data = await response.json();
      setBrands(data.brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products?category=${selectedCategory}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      const data = await response.json();
      setProducts(data.products || []);
      applyFilters(data.products || [], selectedBrand, selectedDisplayType);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (productsList, brandFilter, displayTypeFilter) => {
    let filtered = productsList;
    
    // Apply brand filter
    if (brandFilter !== 'all') {
      filtered = filtered.filter(p => p.brand_id === brandFilter);
    }
    
    // Apply display type filter
    if (displayTypeFilter !== 'all') {
      const displayTypeOption = displayTypes.find(dt => dt.value === displayTypeFilter);
      if (displayTypeOption && displayTypeOption.keywords) {
        filtered = filtered.filter(p => {
          const productDisplayType = (p.features?.['Display Type'] || p.display_type || '').toLowerCase();
          return displayTypeOption.keywords.some(keyword => productDisplayType.includes(keyword.toLowerCase()));
        });
      }
    }
    
    setFilteredProducts(filtered);
  };

  const handleBrandChange = (brandId) => {
    setSelectedBrand(brandId);
    setSelectedProducts([]);
    applyFilters(products, brandId, selectedDisplayType);
  };

  const handleDisplayTypeChange = (displayType) => {
    setSelectedDisplayType(displayType);
    setSelectedProducts([]);
    applyFilters(products, selectedBrand, displayType);
  };

  const fetchVariants = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-variants`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      const data = await response.json();
      setVariants(data.variants || []);
      setFilteredVariants(data.variants || []);
    } catch (error) {
      console.error('Error fetching variants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const query = searchQuery.toLowerCase().trim();
    
    if (!query) {
      setFilteredProducts(products);
      setFilteredVariants(variants);
      return;
    }

    if (activeTab === 'products') {
      const filtered = products.filter(product => {
        const brandMatch = product.brand_name?.toLowerCase().includes(query);
        const modelMatch = product.model_name?.toLowerCase().includes(query);
        const skuMatch = product.sku_code?.toLowerCase().includes(query);
        return brandMatch || modelMatch || skuMatch;
      });
      setFilteredProducts(filtered);
    } else if (activeTab === 'variants') {
      const filtered = variants.filter(variant => {
        const zupcMatch = variant.zupc_code?.toLowerCase().includes(query);
        const brandMatch = variant.sku_details?.brand_name?.toLowerCase().includes(query);
        const modelMatch = variant.sku_details?.model_name?.toLowerCase().includes(query);
        const skuMatch = variant.sku_id?.toLowerCase().includes(query);
        return zupcMatch || brandMatch || modelMatch || skuMatch;
      });
      setFilteredVariants(filtered);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredProducts(products);
    setFilteredVariants(variants);
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  // Open Channel Modal for Activate/Deactivate
  const openChannelModal = (product, action) => {
    setChannelModalProduct(product);
    setChannelModalAction(action);
    // Pre-select channels based on current status
    if (action === 'deactivate') {
      setSelectedChannels({
        b2b: product.channel_status?.b2b !== false,
        b2r: product.channel_status?.b2r !== false,
        b2c: product.channel_status?.b2c !== false,
        buyback: product.channel_status?.buyback !== false
      });
    } else {
      setSelectedChannels({
        b2b: product.channel_status?.b2b === false,
        b2r: product.channel_status?.b2r === false,
        b2c: product.channel_status?.b2c === false,
        buyback: product.channel_status?.buyback === false
      });
    }
    setShowChannelModal(true);
  };

  const closeChannelModal = () => {
    setShowChannelModal(false);
    setChannelModalProduct(null);
    setSelectedChannels({ b2b: false, b2r: false, b2c: false, buyback: false });
  };

  const handleChannelStatusChange = async () => {
    if (!channelModalProduct) return;
    
    const hasSelection = Object.values(selectedChannels).some(v => v);
    if (!hasSelection) {
      alert('Please select at least one channel');
      return;
    }

    try {
      setChannelLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${channelModalProduct.id}/channel-status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            action: channelModalAction,
            channels: selectedChannels
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        closeChannelModal();
        fetchProducts();
      } else {
        alert('Failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to update channel status');
    } finally {
      setChannelLoading(false);
    }
  };

  const handleBulkStatusChange = async (isActive) => {
    if (selectedProducts.length === 0) {
      alert('Please select products first');
      return;
    }

    if (!window.confirm(`${isActive ? 'Activate' : 'Deactivate'} ${selectedProducts.length} product(s)?`)) {
      return;
    }

    try {
      setBulkLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/bulk-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            product_ids: selectedProducts,
            is_active: isActive
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(`✓ ${data.message}`);
        setSelectedProducts([]);
        fetchProducts();
      } else {
        alert('Failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to update products');
    } finally {
      setBulkLoading(false);
    }
  };

  // Get channel status display
  const getChannelStatusBadges = (product) => {
    const status = product.channel_status || { b2b: true, b2r: true, b2c: true, buyback: true };
    return (
      <div className="flex gap-1">
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${status.b2b !== false ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400 line-through'}`}>B2B</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${status.b2r !== false ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-400 line-through'}`}>B2R</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${status.b2c !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400 line-through'}`}>B2C</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${status.buyback !== false ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400 line-through'}`}>Buyback</span>
      </div>
    );
  };

  const getInventoryHierarchy = (variantsToUse = variants) => {
    const hierarchy = {};
    
    variantsToUse.forEach(variant => {
      const skuName = variant.sku_details?.model_name || 'Unknown';
      const skuId = variant.sku_id;
      
      if (!hierarchy[skuId]) {
        hierarchy[skuId] = {
          name: skuName,
          totalQty: 0,
          zupcs: {}
        };
      }
      
      const zupcCode = variant.zupc_code || 'Unknown';
      if (!hierarchy[skuId].zupcs[zupcCode]) {
        hierarchy[skuId].zupcs[zupcCode] = {
          details: variant,
          qty: 0
        };
      }
    });
    
    return hierarchy;
  };

  const getCategoryIcon = () => {
    const cat = categories.find(c => c.name === selectedCategory);
    if (cat) {
      const Icon = cat.icon;
      return <Icon className="w-5 h-5" />;
    }
    return <Package className="w-5 h-5" />;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 bg-[#2b3380] rounded-xl flex items-center justify-center text-white">
                {getCategoryIcon()}
              </div>
              Product Management
            </h1>
            <p className="text-gray-600 mt-1">Manage SKUs and product variants</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Category Selector */}
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2b3380] focus:border-transparent font-medium text-gray-700 cursor-pointer"
                data-testid="category-selector"
              >
                {categories.map(cat => (
                  <option key={cat.name} value={cat.name}>{cat.name}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            
            {/* Add Product Button */}
            <button
              onClick={() => setShowScrapeModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#2b3380] text-white rounded-xl hover:bg-[#1e2560] transition-colors font-medium shadow-sm"
              data-testid="add-product-btn"
            >
              <Plus className="w-5 h-5" />
              Add Product
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-500">Total Products</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                <p className="text-sm text-gray-500">Active</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.withPrice}</p>
                <p className="text-sm text-gray-500">With MRP</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Tag className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{brands.length}</p>
                <p className="text-sm text-gray-500">Brands</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6">
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => handleTabChange('products')}
              className={`flex-1 py-4 px-6 font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                activeTab === 'products'
                  ? 'border-b-2 border-[#2b3380] text-[#2b3380] bg-[#2b3380]/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="products-tab"
            >
              <Package className="w-5 h-5" />
              Products (SKU)
              <span className="ml-1 text-xs px-2 py-0.5 bg-gray-100 rounded-full">{products.length}</span>
            </button>
            <button
              onClick={() => handleTabChange('variants')}
              className={`flex-1 py-4 px-6 font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                activeTab === 'variants'
                  ? 'border-b-2 border-[#2b3380] text-[#2b3380] bg-[#2b3380]/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="variants-tab"
            >
              <Grid3x3 className="w-5 h-5" />
              Variants (ZUPC)
              <span className="ml-1 text-xs px-2 py-0.5 bg-gray-100 rounded-full">{variants.length}</span>
            </button>
          </div>
        </div>

        {/* Brand Filter Tabs - For Products tab */}
        {activeTab === 'products' && brands.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6 overflow-hidden">
            <div className="flex overflow-x-auto scrollbar-hide">
              <button
                onClick={() => handleBrandChange('all')}
                className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-all border-b-2 ${
                  selectedBrand === 'all'
                    ? 'border-[#2b3380] text-[#2b3380] bg-[#2b3380]/5'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                All Brands
                <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 rounded-full">{products.length}</span>
              </button>

              {brands.map(brand => {
                const brandProductCount = products.filter(p => p.brand_id === brand.id).length;
                if (brandProductCount === 0) return null;
                
                return (
                  <button
                    key={brand.id}
                    onClick={() => handleBrandChange(brand.id)}
                    className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-all border-b-2 flex items-center gap-2 ${
                      selectedBrand === brand.id
                        ? 'border-[#2b3380] text-[#2b3380] bg-[#2b3380]/5'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {brand.logo_url && (
                      <img src={brand.logo_url} alt={brand.name} className="w-5 h-5 object-contain rounded" />
                    )}
                    {brand.name}
                    <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full">{brandProductCount}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Search & Filters */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <ProductSearchDropdown
                  products={filteredProducts}
                  onSelect={(product) => navigate(`/products/${product.id}`)}
                  placeholder="Search by SKU Code or Product Name..."
                />
              </div>
              
              <div className="flex items-center gap-2">
                {/* Display Type Filter */}
                <div className="relative">
                  <select
                    value={selectedDisplayType}
                    onChange={(e) => handleDisplayTypeChange(e.target.value)}
                    className="appearance-none pl-9 pr-8 py-2.5 border border-gray-200 rounded-lg bg-white text-sm font-medium text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380] cursor-pointer"
                    data-testid="display-type-filter"
                  >
                    {displayTypes.map(dt => (
                      <option key={dt.value} value={dt.value}>{dt.label}</option>
                    ))}
                  </select>
                  <Monitor className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-[#2b3380]' : 'text-gray-500'}`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-[#2b3380]' : 'text-gray-500'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Refresh */}
                <button
                  onClick={fetchProducts}
                  className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid="refresh-products"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            
            {/* Active Filter Indicator */}
            {selectedDisplayType !== 'all' && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                <span className="text-sm text-gray-500">Filtering by:</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                  <Monitor className="w-3.5 h-3.5" />
                  {displayTypes.find(dt => dt.value === selectedDisplayType)?.label}
                  <button
                    onClick={() => handleDisplayTypeChange('all')}
                    className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
                <span className="text-sm text-gray-400">({filteredProducts.length} products)</span>
              </div>
            )}
          </div>
        )}

        {/* Products Tab Content */}
        {activeTab === 'products' && (
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-[#2b3380]" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500 mb-6">Add your first product from Smartprix</p>
                <button
                  onClick={() => setShowScrapeModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2b3380] text-white rounded-xl hover:bg-[#1e2560] transition-colors font-medium"
                >
                  <Plus className="w-5 h-5" />
                  Add Product
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              /* Grid View */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-lg transition-all group"
                    data-testid={`product-card-${product.id}`}
                  >
                    {/* Product Image */}
                    <div 
                      className="h-40 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4 relative cursor-pointer"
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      {product.images?.[0] ? (
                        <img
                          src={product.images[0]}
                          alt={product.model_name}
                          className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <Package className="w-16 h-16 text-gray-300" />
                      )}
                      
                      {/* Status Badge */}
                      <span className={`absolute top-3 right-3 text-xs px-2 py-1 rounded-full font-medium ${
                        product.is_active === false 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {product.is_active === false ? 'Inactive' : 'Active'}
                      </span>
                    </div>
                    
                    {/* Product Info */}
                    <div className="p-4">
                      {/* Brand */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {product.brand_logo && (
                            <img src={product.brand_logo} alt="" className="w-5 h-5 object-contain rounded" />
                          )}
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{product.brand_name}</span>
                        </div>
                        {/* Channel Status */}
                        {getChannelStatusBadges(product)}
                      </div>
                      
                      {/* Model Name */}
                      <h3 
                        className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-[#2b3380] transition-colors cursor-pointer"
                        onClick={() => navigate(`/products/${product.id}`)}
                      >
                        {product.model_name}
                      </h3>
                      
                      {/* SKU & Display Type */}
                      <div className="flex items-center gap-2 mb-3">
                        <p className="text-xs text-gray-500 font-mono">{product.sku_code}</p>
                        {(product.features?.['Display Type'] || product.display_type) && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            (product.features?.['Display Type'] || product.display_type || '').toLowerCase().includes('amoled')
                              ? 'bg-purple-100 text-purple-700'
                              : (product.features?.['Display Type'] || product.display_type || '').toLowerCase().includes('oled')
                              ? 'bg-indigo-100 text-indigo-700'
                              : (product.features?.['Display Type'] || product.display_type || '').toLowerCase().includes('retina')
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {product.features?.['Display Type'] || product.display_type}
                          </span>
                        )}
                      </div>
                      
                      {/* Price & Variants */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div>
                          {product.mrp ? (
                            <span className="text-lg font-bold text-gray-900">₹{product.mrp.toLocaleString()}</span>
                          ) : product.store_prices?.[0]?.price ? (
                            <span className="text-lg font-bold text-gray-900">₹{product.store_prices[0].price.toLocaleString()}</span>
                          ) : (
                            <span className="text-sm text-gray-400">No price</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Grid3x3 className="w-3.5 h-3.5" />
                          {product.variants_count || 0} variants
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/products/${product.id}`);
                          }}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        {/* Show Activate button if any channel is inactive, otherwise show Deactivate */}
                        {(product.channel_status?.b2b === false || product.channel_status?.b2r === false || product.channel_status?.b2c === false || product.channel_status?.buyback === false) ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openChannelModal(product, 'activate');
                            }}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                          >
                            <Power className="w-4 h-4" />
                            Activate
                          </button>
                        ) : null}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openChannelModal(product, 'deactivate');
                          }}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <PowerOff className="w-4 h-4" />
                          Deactivate
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* List View */
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">SKU Code</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Brand</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">MRP</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Channels</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredProducts.map(product => (
                      <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              {product.images?.[0] ? (
                                <img src={product.images[0]} alt="" className="w-10 h-10 object-contain" />
                              ) : (
                                <Package className="w-6 h-6 text-gray-400" />
                              )}
                            </div>
                            <span className="font-medium text-gray-900 line-clamp-1">{product.model_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm font-mono text-gray-600">{product.sku_code}</span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {product.brand_logo && (
                              <img src={product.brand_logo} alt="" className="w-5 h-5 object-contain rounded" />
                            )}
                            <span className="text-sm text-gray-700">{product.brand_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right">
                          {product.mrp ? (
                            <span className="font-semibold text-gray-900">₹{product.mrp.toLocaleString()}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center">
                          {getChannelStatusBadges(product)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/products/${product.id}`);
                              }}
                              className="p-2 text-[#2b3380] hover:bg-[#2b3380]/10 rounded-lg transition-colors"
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {/* Show Activate button if any channel is inactive */}
                            {(product.channel_status?.b2b === false || product.channel_status?.b2r === false || product.channel_status?.b2c === false || product.channel_status?.buyback === false) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openChannelModal(product, 'activate');
                                }}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Activate"
                              >
                                <Power className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openChannelModal(product, 'deactivate');
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Deactivate"
                            >
                              <PowerOff className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Variants Tab Content */}
        {activeTab === 'variants' && (
          <div>
            {/* Search Bar */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search by ZUPC, Brand, Model, or SKU..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                    data-testid="variant-search-input"
                  />
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
                <button
                  onClick={handleSearch}
                  className="px-6 py-2.5 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560] transition-colors flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-sm text-gray-500 mt-2">Found {filteredVariants.length} variant(s)</p>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-[#2b3380]" />
              </div>
            ) : filteredVariants.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
                <Grid3x3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No variants found</h3>
                <p className="text-gray-500">Variants are created automatically when you add products</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ZUPC Code</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Storage</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Colors</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">MRP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredVariants.map(variant => (
                      <tr key={variant.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4">
                          <span className="font-mono text-sm text-[#2b3380] font-medium">{variant.zupc_code}</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-700">{variant.sku_details?.model_name || '-'}</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm font-medium text-gray-900">{variant.storage || variant.memory || '-'}</span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1">
                            {variant.available_colors?.slice(0, 2).map((color, idx) => (
                              <span key={idx} className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">{color}</span>
                            ))}
                            {variant.available_colors?.length > 2 && (
                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">+{variant.available_colors.length - 2}</span>
                            )}
                            {!variant.available_colors?.length && variant.color && (
                              <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">{variant.color}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right">
                          {variant.mrp ? (
                            <span className="font-semibold text-gray-900">₹{variant.mrp.toLocaleString()}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Add Product Modal */}
        <ProductScrapeModal
          isOpen={showScrapeModal}
          onClose={() => setShowScrapeModal(false)}
          category={selectedCategory}
          onProductCreated={() => {
            fetchProducts();
            fetchBrands();
          }}
        />

        {/* Channel Status Modal */}
        {showChannelModal && channelModalProduct && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {channelModalAction === 'deactivate' ? 'Deactivate Product' : 'Activate Product'}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {channelModalProduct.model_name}
                    </p>
                  </div>
                  <button
                    onClick={closeChannelModal}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  {channelModalAction === 'deactivate' 
                    ? 'Select which channels to deactivate this product from:'
                    : 'Select which channels to activate this product for:'}
                </p>
                
                <div className="space-y-3">
                  {channels.map(channel => {
                    const Icon = channel.icon;
                    const isSelected = selectedChannels[channel.key];
                    
                    return (
                      <label
                        key={channel.key}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected 
                            ? channelModalAction === 'deactivate'
                              ? 'border-red-300 bg-red-50'
                              : 'border-green-300 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => setSelectedChannels(prev => ({
                            ...prev,
                            [channel.key]: e.target.checked
                          }))}
                          className={`w-5 h-5 rounded ${
                            channelModalAction === 'deactivate' 
                              ? 'text-red-600 focus:ring-red-500' 
                              : 'text-green-600 focus:ring-green-500'
                          }`}
                        />
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          channel.color === 'blue' ? 'bg-blue-100' :
                          channel.color === 'purple' ? 'bg-purple-100' :
                          channel.color === 'orange' ? 'bg-orange-100' :
                          'bg-green-100'
                        }`}>
                          <Icon className={`w-5 h-5 ${
                            channel.color === 'blue' ? 'text-blue-600' :
                            channel.color === 'purple' ? 'text-purple-600' :
                            channel.color === 'orange' ? 'text-orange-600' :
                            'text-green-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{channel.name}</p>
                          <p className="text-sm text-gray-500">{channel.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={closeChannelModal}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChannelStatusChange}
                    disabled={channelLoading || !Object.values(selectedChannels).some(v => v)}
                    className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                      channelModalAction === 'deactivate'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {channelLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : channelModalAction === 'deactivate' ? (
                      <PowerOff className="w-4 h-4" />
                    ) : (
                      <Power className="w-4 h-4" />
                    )}
                    {channelModalAction === 'deactivate' ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductManagement;
